import CircularProgress from "@mui/material/CircularProgress";
import { t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { JaktomradeResponse } from "../../api/types";
import AppDataContext from "../../contexts/AppDataContext/DataProvider";
import { harJaktPaArt } from "../../helpers/filters";
import type { ArtType } from "../../helpers/types";
import { OmradeTypeForArt } from "../../helpers/types";
import { Stateful, useStateful } from "../../hooks/useStateful";
import i18n from "../../i18n";
import AlertDefault, { AlertVariant } from "../Alerts/AlertDefault";
import ButtonDefault from "../Button/ButtonDefault";
import Combobox, { ComboboxOption } from "../Form/Combobox/Combobox";
import ReadOnlyField from "../Form/ReadOnlyField";
import Jaktomrade from "../Jaktomrade/Jaktomrade";
import BoldText from "../Text/BoldText";
import ErrorText from "../Text/ErrorText";
import Text, { TextVariant } from "../Text/Text";

type AreaPickerProps = {
  onChange: (selected: ComboboxOption<JaktomradeResponse> | null) => void;
  valgtArt?: ArtType;
  defaultOmradekode?: string;
  helperText?: string;
  errorText?: string;
  disabled?: boolean;
  selectedState?: Stateful<ComboboxOption>;
  visAlleOmrader?: boolean;
  onDarkBg?: boolean;
  readOnly?: boolean;
  className?: string;
};

export default function JaktomradePicker({
  onChange,
  valgtArt,
  helperText,
  errorText,
  defaultOmradekode,
  disabled = false,
  selectedState,
  visAlleOmrader = false,
  onDarkBg,
  readOnly,
  className,
}: AreaPickerProps): JSX.Element {
  const navigate = useNavigate();
  const data = React.useContext(AppDataContext);
  const initialSelected = selectedState?.value || {
    label: "",
    id: "",
    data: {} as JaktomradeResponse,
  };

  const [selected, setSelected] = useState<ComboboxOption>(initialSelected);
  const open = useStateful(false);

  const options = useMemo(() => {
    if (data.jaktområder.data !== undefined) {
      const _options: ComboboxOption<JaktomradeResponse>[] = [];

      if (!visAlleOmrader) {
        // bare favorittområder
        [...data.jaktområder.data].forEach((omrade) => {
          if (!valgtArt || harJaktPaArt(valgtArt, omrade)) {
            _options.push({
              label: omrade.Områdenavn ?? ("Navn" in omrade ? (omrade.Navn as string) : ""),
              id: omrade.Områdekode ?? "",
              data: omrade,
            });
          }
        });
      } else {
        // all the områder
        data.vald.data?.data
          .concat(data.jaktfelt.data?.data.concat(data.villreinvald.data?.data || []) || [])
          .forEach((omrade) => {
            if (!valgtArt || harJaktPaArt(valgtArt, omrade)) {
              _options.push({
                label: "Navn" in omrade ? omrade.Navn || "" : "",
                id: omrade.Id ?? "",
                data: omrade,
              });
            }
          });
      }

      const sortedOptions = _options.sort((a, b) => {
        if (a?.label && b?.label) {
          return a.label.localeCompare(b.label, i18n.language);
        } else {
          return 0;
        }
      });
      return sortedOptions;
    }
    return [];
  }, [data.jaktområder.data, valgtArt]);

  useEffect(() => {
    onChange(selected);

    if (selectedState) {
      selectedState.set(selected);
    }
  }, [selected]);

  useEffect(() => {
    const selectedOption =
      options.length === 1 ? options[0] : options.find((opt) => opt.id === defaultOmradekode);
    if (selectedOption) {
      setSelected(selectedOption);
    }
  }, [options, defaultOmradekode]);

  if (data.jaktområder.isLoading) {
    return (
      <div className="w-full text-center">
        <CircularProgress thickness={7} size={40} />
      </div>
    );
  }
  let jaktomradeType = valgtArt ? OmradeTypeForArt[valgtArt] : " Jaktområde";
  jaktomradeType = jaktomradeType === "Villreinvald" ? "Vald" : jaktomradeType;

  if (readOnly) {
    return (
      <ReadOnlyField
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
            role="graphics-symbol"
          >
            <path
              d="M0 24V5.64706L5.55556 9.88235L12.5 0L19.4444 5.64706H25V24H0ZM6.94444 19.7647L12.5 12L22.2222 19.6941V8.47059H18.4722L13.0556 4.05882L6.18056 13.8706L2.77778 11.2941V16.3765L6.94444 19.7647Z"
              fill="#222222"
            />
          </svg>
        }
        label={jaktomradeType}
        value={`${selected.label || initialSelected.label} ${selected.id || initialSelected.id}`}
      />
    );
  }

  return (
    <div className={className}>
      {options.length > 0 ? (
        <>
          <Combobox
            ariadescribedby={"helptext-jaktområdepicker"}
            placeholder={
              options.length == 0
                ? t("components.jaktomradePicker.noOptionsText")
                : t("actions.search")
            }
            showId={true}
            onDarkBg={onDarkBg}
            options={options}
            disabled={disabled || readOnly || options.length == 0}
            onChange={(value) => {
              setSelected(value ?? { id: "", label: "", data: {} as JaktomradeResponse });
            }}
            label={`${!disabled ? t("actions.select") : ""} ${
              !disabled ? jaktomradeType.toLocaleLowerCase() : jaktomradeType
            }`}
            defaultValue={selected || initialSelected}
            noOptionsText={t("components.jaktomradePicker.noOptionsText")}
            renderOption={(props, option: ComboboxOption<JaktomradeResponse>, state) => {
              if (option.id && option.label) {
                return (
                  <li {...props} key={state.index} className={` ${props.className} w-full`}>
                    <Jaktomrade isSelected={state.selected} områdeEntry={{ ...option.data }} />
                  </li>
                );
              }
              return <li key={state.index} />;
            }}
            open={open}
            showIcon={!disabled}
            errorHelpText={errorText}
            helpText={helperText}
          />
        </>
      ) : (
        <div>
          <Text className={`text-xl mb-px ${onDarkBg ? "text-white" : ""}`}>{`${t(
            "actions.select",
          )} ${jaktomradeType.toLowerCase()}`}</Text>
          <AlertDefault
            variant={AlertVariant.Warning}
            bottomElement={
              <ButtonDefault
                id="leggTilJaktområde"
                onClick={async () => navigate("/minside/jaktomrader")}
                label={t("pages.jaktområder.title")}
              />
            }
          >
            {valgtArt ? (
              <BoldText
                text={t("components.jaktomradePicker.noOptionsArtHelpText", {
                  art: valgtArt?.toLowerCase(),
                })}
                specificWords={[2, 3, 4, 5]}
                variant={TextVariant.small}
              />
            ) : (
              t("components.jaktomradePicker.noOptionsHelpText")
            )}
          </AlertDefault>
          {errorText && <ErrorText>{errorText}</ErrorText>}
        </div>
      )}
    </div>
  );
}
